// PolicyChainEvaluator — validates policy trigger→command chains at runtime.
//
// FACET-ADR-018 §3: policy_chain assertions.
// Moment owns structural validation (ADR-030). Facet proves behavioral correctness:
// when the triggering event occurs, does the chained command execute, and is
// there a causal link between them?
//
// Truthfulness rules (falsifiability overhaul):
//  - No definitions artifact available → ONE explicit tier SKIP record, never
//    silence and never a heuristic pass.
//  - Definitions present but the trigger never occurs → explicit SKIP record
//    ("not exercised" is not evidence of correctness).
//  - EVERY occurrence of a trigger is evaluated, not just the first — a policy
//    that fires on the first occurrence but not on a later one FAILS.
//  - Every declared chained command (chainsTo is a list) is verified per
//    trigger occurrence.

import type { PlatformEvent } from '../../types/index.ts';
import type { AssertionType } from '../../types/simulation-results.ts';

// ---------------------------------------------------------------------------
// Policy definition (from Moment's policy-definitions.json artifact)
// ---------------------------------------------------------------------------

export interface PolicyDefinition {
  policyName: string;
  context: string;
  /** Event type that triggers the policy. */
  trigger: string;
  /** Command name(s) the policy chains to — each must be verified. */
  chainsTo: string[];
  actionDescription?: string;
}

/**
 * Parse/normalize raw `policy-definitions.json` content. The artifact contract
 * (Moment `simulate` output, sibling of saga-state-machines.json) is an array
 * of `{ name, contextId, trigger, actionDescription?, chainsTo: string[] }`.
 * A legacy internal shape (`{ policyName, context, chainsTo: string }`) is
 * also accepted. Returns null when the data does not conform — callers must
 * treat that exactly like an absent artifact (explicit tier skip), never as
 * an empty-but-valid declaration.
 */
export function parsePolicyDefinitions(data: unknown): PolicyDefinition[] | null {
  if (!Array.isArray(data)) return null;
  const out: PolicyDefinition[] = [];
  for (const raw of data) {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const policyName =
      typeof r.name === 'string' && r.name
        ? r.name
        : typeof r.policyName === 'string' && r.policyName
          ? r.policyName
          : null;
    const context =
      typeof r.contextId === 'string'
        ? r.contextId
        : typeof r.context === 'string'
          ? r.context
          : '';
    const trigger = typeof r.trigger === 'string' && r.trigger ? r.trigger : null;
    const chainsTo = Array.isArray(r.chainsTo)
      ? r.chainsTo.filter((c): c is string => typeof c === 'string' && c.length > 0)
      : typeof r.chainsTo === 'string' && r.chainsTo
        ? [r.chainsTo]
        : null;
    if (!policyName || !trigger || !chainsTo || chainsTo.length === 0) return null;
    out.push({
      policyName,
      context,
      trigger,
      chainsTo,
      actionDescription: typeof r.actionDescription === 'string' ? r.actionDescription : undefined,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Assertion output
// ---------------------------------------------------------------------------

export interface PolicyAssertionOutput {
  assertionId: string;
  assertionType: AssertionType;
  passed: boolean;
  /** True when the assertion could not be evaluated (reason in detail). */
  skipped?: boolean;
  detail: string;
  policyName: string;
}

// ---------------------------------------------------------------------------
// Event type normalization — strip SimProcess prefix from both sides
// ---------------------------------------------------------------------------

function normalizeEventType(eventType: string): string {
  return eventType.replace(/^SimProcess\./, '');
}

function eventTypeMatches(actual: string, expected: string): boolean {
  return normalizeEventType(actual) === normalizeEventType(expected);
}

// ---------------------------------------------------------------------------
// Causal link verification — BFS with per-node depth tracking
// ---------------------------------------------------------------------------

/**
 * Check if targetEventId is causally linked to sourceEventId —
 * either directly (sourceEventId in target's causationEventIds) or
 * transitively (following the causation chain up to maxDepth hops).
 */
function isCausallyLinked(
  sourceEventId: string,
  targetEvent: PlatformEvent,
  eventIndex: Map<string, PlatformEvent>,
  maxDepth: number = 10,
): boolean {
  const visited = new Set<string>();
  const queue = targetEvent.causationEventIds.map((eventId) => ({
    eventId,
    depth: 1,
  }));

  while (queue.length > 0) {
    const current = queue.shift()!;
    const { eventId: currentId, depth } = current;

    if (depth > maxDepth) continue;
    if (currentId === sourceEventId) return true;
    if (visited.has(currentId)) continue;
    visited.add(currentId);

    const currentEvent = eventIndex.get(currentId);
    if (currentEvent && depth < maxDepth) {
      queue.push(
        ...currentEvent.causationEventIds.map((eventId) => ({
          eventId,
          depth: depth + 1,
        })),
      );
    }
  }

  return false;
}

// ---------------------------------------------------------------------------
// Evaluate policy chains across a full trace
// ---------------------------------------------------------------------------

/**
 * For each policy definition, find EVERY trigger occurrence in the trace and
 * verify each declared chained command appears after it with a causal link.
 *
 * `definitions === null/undefined` means the policy-definitions artifact is
 * not available (old artifact set, missing/malformed file): one explicit tier
 * SKIP record is emitted so the tier is visible, never silent. An empty array
 * means the artifact explicitly declares zero policies — nothing to verify,
 * no records (truthful silence).
 */
export function evaluatePolicyChains(
  events: PlatformEvent[],
  definitions: PolicyDefinition[] | null | undefined,
): PolicyAssertionOutput[] {
  if (definitions == null) {
    if (events.length === 0) return [];
    return [
      {
        assertionId: 'policy-chain-defs-missing',
        assertionType: 'policy_chain',
        passed: false,
        skipped: true,
        detail:
          'No policy definitions artifact available (policy-definitions.json) — policy chain tier skipped',
        policyName: '',
      },
    ];
  }

  const results: PolicyAssertionOutput[] = [];

  // Build event index for causation chain traversal
  const eventIndex = new Map<string, PlatformEvent>();
  for (const event of events) {
    eventIndex.set(event.eventId, event);
  }

  for (const policy of definitions) {
    // Defensive: tolerate definitions that arrived without passing through
    // parsePolicyDefinitions — the artifact contract names the field `name`,
    // the internal shape names it `policyName`. Never print "undefined".
    const policyName =
      typeof policy.policyName === 'string' && policy.policyName
        ? policy.policyName
        : ((policy as unknown as { name?: string }).name ?? '');
    // Defensive: tolerate legacy single-command definitions at runtime.
    const chainedCommands = Array.isArray(policy.chainsTo)
      ? policy.chainsTo
      : [policy.chainsTo as unknown as string];

    // Find EVERY trigger occurrence in the trace
    const triggerIndices: number[] = [];
    for (let i = 0; i < events.length; i++) {
      if (eventTypeMatches(events[i].eventType, policy.trigger)) triggerIndices.push(i);
    }

    if (triggerIndices.length === 0) {
      // Unexercised trigger — NOT a pass. The chain was never observed, so
      // nothing was verified; emit an explicit skip.
      results.push({
        assertionId: `policy-chain-${policyName}-trigger`,
        assertionType: 'policy_chain',
        passed: false,
        skipped: true,
        detail: `Policy "${policyName}": trigger "${policy.trigger}" never occurs in the trace — chain not exercised, nothing verified`,
        policyName,
      });
      continue;
    }

    // Scenario-representation gap (Moment-side, documented): generated
    // scenarios do not synthesize policy-chain effects into the trace. A
    // chained command that appears NOWHERE in the events was never claimed by
    // the scenario — that is unverifiable, not a defect: ONE explicit SKIP per
    // (policy, command). FAIL is reserved for commands that ARE present in the
    // trace but lack the causal link to a trigger occurrence (keeps the
    // causation-break mutation falsifiable).
    const representedCommands: string[] = [];
    for (const command of chainedCommands) {
      const cmdSuffix = chainedCommands.length > 1 ? `-${normalizeEventType(command)}` : '';
      const commandInTrace = events.some((e) => eventTypeMatches(e.eventType, command));
      if (!commandInTrace) {
        results.push({
          assertionId: `policy-chain-${policyName}${cmdSuffix}-command`,
          assertionType: 'policy_chain',
          passed: false,
          skipped: true,
          detail: `Policy "${policyName}": trigger "${policy.trigger}" fired but chained command "${command}" appears nowhere in the trace — chain not represented in this scenario's events`,
          policyName,
        });
      } else {
        representedCommands.push(command);
      }
    }

    triggerIndices.forEach((triggerIndex, occurrence) => {
      const triggerEvent = events[triggerIndex];
      // Occurrence suffix keeps assertion ids unique when a trigger fires
      // more than once (each occurrence is evaluated independently).
      const occSuffix = triggerIndices.length > 1 ? `-t${occurrence}` : '';

      for (const command of representedCommands) {
        const cmdSuffix = chainedCommands.length > 1 ? `-${normalizeEventType(command)}` : '';
        const idBase = `policy-chain-${policyName}${cmdSuffix}${occSuffix}`;

        // Find all matching command events after this trigger occurrence
        const candidateCommands = events
          .slice(triggerIndex + 1)
          .filter((e) => eventTypeMatches(e.eventType, command));

        if (candidateCommands.length === 0) {
          // The command appears in the trace but only BEFORE this trigger
          // occurrence — a pre-trigger occurrence is an unrelated flow step,
          // never a claimed chain effect. Same representation gap as the
          // absent-command case: SKIP. FAIL is reserved for candidates AFTER
          // the trigger that lack the causal link (the falsifiable case).
          results.push({
            assertionId: `${idBase}-command`,
            assertionType: 'policy_chain',
            passed: false,
            skipped: true,
            detail: `Policy "${policyName}": trigger "${policy.trigger}" fired (occurrence ${occurrence + 1}/${triggerIndices.length}) but no "${command}" follows it in the trace — chain not represented in this scenario's events`,
            policyName,
          });
          continue;
        }

        // Check if any candidate command has a causal link to THIS trigger occurrence
        const linkedCommand = candidateCommands.find((cmd) =>
          isCausallyLinked(triggerEvent.eventId, cmd, eventIndex),
        );

        if (linkedCommand) {
          results.push({
            assertionId: idBase,
            assertionType: 'policy_chain',
            passed: true,
            detail: `Policy "${policyName}": "${policy.trigger}" → "${command}" with verified causal link (occurrence ${occurrence + 1}/${triggerIndices.length})`,
            policyName,
          });
        } else {
          results.push({
            assertionId: `${idBase}-causation`,
            assertionType: 'policy_chain',
            passed: false,
            detail: `Policy "${policyName}": ${candidateCommands.length} "${command}" event(s) found after "${policy.trigger}" (occurrence ${occurrence + 1}/${triggerIndices.length}) but none have a causal link in causationEventIds chain`,
            policyName,
          });
        }
      }
    });
  }

  return results;
}
